import "./styles/FeaturedToolsSection.scss";

import { GridContainer } from "@amzn/stencil-react-components/layout";
import { Col } from "@amzn/stencil-react-components/layout";
import { LinkTextDecorationOptions } from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import { AiForMeItem } from "common/components/AIForMe/AiForMeItem";
import { InternalLink } from "common/components/InternalLink/InternalLink";
import { useCompare } from "common/context/compare";
import { TrackClickInteraction } from "common/helpers";
import { VoteHandler } from "common/hooks";
import { Paths } from "common/paths/paths";
import { getString } from "common/uistringlabels/uiStringUtils";
import { AIForMeTool, SORTING_PRIORITY } from "lib/models/aiforme-models";
import { CatalogicalService } from "lib/services";
import React, { FC, useMemo } from "react";

import { FeaturedTabConfig, FeaturedToolsTabs } from "./FeaturedToolsTabs";

export interface FeaturedToolsSectionProps {
  roleTools: AIForMeTool[];
  orgTools: AIForMeTool[];
  amazonPickedTools: AIForMeTool[];
  userRole: string;
  voteHandler: VoteHandler;
  client: CatalogicalService<AIForMeTool>;
  /** Callback to track click interactions when user clicks "Learn More" */
  onTrackClick: TrackClickInteraction;
}

export const FeaturedToolsSection: FC<FeaturedToolsSectionProps> = ({
  roleTools,
  orgTools,
  amazonPickedTools,
  userRole,
  voteHandler,
  client,
  onTrackClick,
}) => {
  const { addTool, removeTool, selectedToolIds, canAddMore, setReviewFlyoutOpen } = useCompare();

  const tabConfigs: FeaturedTabConfig[] = useMemo(() => {
    const hasRoleTools = roleTools.length > 0;
    const hasOrgTools = orgTools.length > 0;

    const amazonPickedConfig: FeaturedTabConfig = {
      label: getString("aiForMe.landingPage.featuredToolsSection.tabs.amazonPicked"),
      tooltip: getString("aiForMe.landingPage.featuredToolsSection.tabs.amazonPickedTooltip"),
      tools: amazonPickedTools,
      filterParam: "amazon-picked",
      touchpoint: "view-more-amazon-picked",
    };

    const roleConfig: FeaturedTabConfig = {
      label: getString("aiForMe.landingPage.featuredToolsSection.tabs.toolsForYourRole", { userRole: userRole }),
      tooltip: getString("aiForMe.landingPage.featuredToolsSection.tabs.toolsForYourRoleTooltip", {
        userRole: userRole,
      }),
      tools: roleTools,
      filterParam: "role",
      touchpoint: "view-more-role",
    };

    const orgConfig: FeaturedTabConfig = {
      label: getString("aiForMe.landingPage.featuredToolsSection.tabs.toolsForYourOrg"),
      tooltip: getString("aiForMe.landingPage.featuredToolsSection.tabs.toolsForYourOrgTooltip"),
      tools: orgTools,
      filterParam: "org",
      touchpoint: "view-more-org",
    };

    // If both are empty, show only Amazon-picked tab
    if (!hasRoleTools && !hasOrgTools) {
      return [amazonPickedConfig];
    }

    const configs: FeaturedTabConfig[] = [];

    // Add role tab or Amazon-picked fallback
    configs.push(hasRoleTools ? roleConfig : amazonPickedConfig);

    // Add org tab or Amazon-picked fallback (only if role tab is showing role tools)
    if (hasOrgTools) {
      configs.push(orgConfig);
    } else if (hasRoleTools) {
      // Only add Amazon-picked for org if role tab is showing role tools
      configs.push(amazonPickedConfig);
    }

    return configs;
  }, [roleTools, orgTools, amazonPickedTools, userRole]);

  const renderToolGrid = (tools: AIForMeTool[], startIndex: number) => {
    return (
      <GridContainer>
        {tools.map((tool, index) => {
          const isCompareSelected = selectedToolIds.has(tool.taxonomyId);
          return (
            <AiForMeItem
              key={tool.taxonomyId}
              item={tool}
              index={startIndex + index}
              voteHandler={voteHandler}
              client={client}
              gridSpan={{ lg: 4, md: 4 }}
              isCompareSelected={isCompareSelected}
              isCompareDisabled={!canAddMore && !isCompareSelected}
              onCompareAdd={addTool}
              onCompareRemove={removeTool}
              removeQuickRate={true}
              onTrackClick={onTrackClick}
              onReviewFlyoutToggle={setReviewFlyoutOpen}
              sortingPriorityTag={SORTING_PRIORITY.find((sp) => sp.value === tool.sortingPriority)?.label}
            />
          );
        })}
      </GridContainer>
    );
  };

  // Key that forces the entire tab system to re-initialize when tab structure changes
  const tabStructureKey = `tabs-${roleTools.length > 0}-${orgTools.length > 0}`;

  return (
    <Col alignItems="center" rowGap={32} data-testid="featured-tools-section" className="featured-tools-section">
      <Text fontSize="T400" fontWeight="bold" data-testid="featured-tools-heading">
        {getString("aiForMe.landingPage.featuredToolsSection.sectionTitle")}
      </Text>

      <FeaturedToolsTabs key={tabStructureKey} tabConfigs={tabConfigs} renderToolGrid={renderToolGrid} />

      <Col style={{ width: "100%" }} rowGap={24} alignItems="center">
        <Text fontSize="T200" color="var(--st-dt-color-text-secondary)" data-testid="featured-tools-transition-text">
          {getString("aiForMe.landingPage.featuredToolsSection.notFindingWhatYouNeed")}
          <InternalLink
            to={Paths.aiForMeAllAiTools.url}
            testId="header-orders-link"
            textDecoration={LinkTextDecorationOptions.Underline}
          >
            {getString("aiForMe.landingPage.featuredToolsSection.notFindingWhatYouNeedAllAiToolsLinkText")}
          </InternalLink>
        </Text>
      </Col>
    </Col>
  );
};
